import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { type ReactElement, StrictMode } from 'react';
import { UNSTABLE_ToastQueue as ToastQueue } from 'react-aria-components';
import ReactDOM from 'react-dom/client';

import { HttpException } from '@/libs/openapi';
import { TOAST_QUEUE_CONFIG, TOAST_TIMEOUT, type SnackbarProps, type TextButtonProps } from '@/libs/serenity-ui';
import reportWebVitals from '@/report-web-vitals.ts';
import { routeTree } from '@/routeTree.gen';

import '@/libs/serenity-ui/index.css';

export const toastQueue = new ToastQueue<{ message: string; actionProps?: TextButtonProps, variant: SnackbarProps['variant'] }>(TOAST_QUEUE_CONFIG);
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000,
    },
    mutations: {
      onError: (error) => {
        if (error instanceof HttpException && !error.isGlobal) {
          return;
        }

        toastQueue.add({ message: error.message, variant: 'error' }, { timeout: TOAST_TIMEOUT });
      },
    },
  },
});

const router = createRouter({
  routeTree,
  context: {
    queryClient,
    toastQueue,
  },
  defaultPreload: 'intent',
  scrollRestoration: true,
  defaultStructuralSharing: true,
  // Since we're using React Query, we don't want loader calls to ever be stale
  // This will ensure that the loader is always called when the route is preloaded or visited
  defaultPreloadStaleTime: 0,
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }

  interface StaticDataRouteOption {
    contextBarComponent?: string | (() => ReactElement);
    contextBarPendingComponent?: string | (() => ReactElement);
    /** Text to display under circular loader indicator */
    pendingDescription?: string;
  }
}

const rootElement = document.getElementById('app');
if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </StrictMode>,
  );
}

if (process.env.NODE_ENV === 'development') {
  import('react-scan').then(({ scan }) => scan({ enabled: true })).catch(console.error);
}

// To start measuring performance in the app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
